import { makeStyles, mergeClasses, shorthands, tokens } from "@fluentui/react-components";
import type { GateTag, GateTagStatus } from "@/engine/types";
import { forwardRef, type ButtonHTMLAttributes } from "react";

const useStyles = makeStyles({
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    maxWidth: "100%",
    height: "20px",
    padding: "0 8px",
    borderRadius: tokens.borderRadiusCircular,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: tokens.fontSizeBase200,
    lineHeight: "18px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    fontFamily: "inherit",
  },
  unverified: { opacity: 0.8 },
  ready: { backgroundColor: tokens.colorPaletteYellowBackground1, color: tokens.colorPaletteYellowForeground2, ...shorthands.borderColor(tokens.colorPaletteYellowBorder1) },
  passed: { backgroundColor: tokens.colorPaletteGreenBackground1, color: tokens.colorPaletteGreenForeground1, ...shorthands.borderColor(tokens.colorPaletteGreenBorder1) },
  failed: { backgroundColor: tokens.colorPaletteRedBackground1, color: tokens.colorPaletteRedForeground1, ...shorthands.borderColor(tokens.colorPaletteRedBorder1) },
});

export const useShakeStyles = makeStyles({
  shake: {
    animationName: {
      "0%": { transform: "translateX(0)" },
      "20%": { transform: "translateX(-4px)" },
      "40%": { transform: "translateX(4px)" },
      "60%": { transform: "translateX(-3px)" },
      "80%": { transform: "translateX(3px)" },
      "100%": { transform: "translateX(0)" },
    },
    animationDuration: "0.35s",
    animationTimingFunction: "ease-in-out",
  },
});

export const GATE_GLYPH: Record<GateTagStatus, string> = { unverified: "○", ready: "◐", passed: "✓", failed: "✕" };

export function gateChipLabel(gate: Pick<GateTag, "name" | "status">): string {
  const suffix = gate.status === "ready" ? " · 검수 대기" : gate.status === "failed" ? " · 반려" : "";
  return `${GATE_GLYPH[gate.status]} ${gate.name}${suffix}`;
}

type Props = { gate: GateTag } & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children">;

/** 검수 상태 칩. `[○ 품질 검수]` → `[◐ 품질 검수 · 검수 대기]` → `[✓ 품질 검수]` / `[✕ 품질 검수 · 반려]`. */
export const GateTagChip = forwardRef<HTMLButtonElement, Props>(function GateTagChip({ gate, className, ...rest }, ref) {
  const styles = useStyles();
  return (
    <button
      ref={ref}
      type="button"
      className={mergeClasses(styles.chip, styles[gate.status], className)}
      title={gate.status === "failed" && gate.failReason ? gate.failReason : undefined}
      {...rest}
    >
      {gateChipLabel(gate)}
    </button>
  );
});
